import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { logError } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";
import { invalidateShifts } from "../../../invalidateShifts";
import { SHIFT_DISCOVERY_LIST_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { ShiftBottomSheetCardsWrapper } from "../../BottomSheet/CardsWrapper";
import { type BundleShiftPay } from "../types";
import { BundleFailedLoadingCard } from "./BundleFailedLoadingCard";

export interface BundleFailedLoadingSheetProps {
  modalState: UseModalState;
  bundleShiftPays: Map<string, BundleShiftPay>;
}

export function BundleFailedLoadingSheet(props: BundleFailedLoadingSheetProps) {
  const { modalState, bundleShiftPays } = props;
  const history = useHistory();

  const { shiftId, shift, bundle } = useShiftModalsDataContext();

  const queryClient = useQueryClient();

  useEffect(() => {
    void invalidateShifts(queryClient);

    logError(APP_V2_APP_EVENTS.SHIFT_BUNDLE_FAILED_TO_LOAD, {
      error: new Error("Bundle failed to load"),
      metadata: {
        shiftId,
        hasOffer: isDefined(shift?.offer),
        bundleShiftPaysCount: bundleShiftPays?.size,
        bundleSize: bundle?.totalShiftCount,
      },
    });
    // shift is replaced by shiftId for stable reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, shiftId]);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <Stack direction="row" sx={{ width: "100%", justifyContent: "center" }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                history.replace(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`);
              }}
            >
              Reload
            </Button>
          </Stack>
        </DialogFooter>
      }
    >
      <ShiftBottomSheetCardsWrapper>
        <BundleFailedLoadingCard />
      </ShiftBottomSheetCardsWrapper>
    </BottomSheet>
  );
}
